import { useState } from "react";
import { Dropdown, Form } from "react-bootstrap";
import ReactDatetimeClass from "../../../../utils/reactDatetime";

import {
	BoolToggleState,
	FilterPopover,
	ThreeWayToggle,
	useFilterPopover,
} from "./filterPopover";
import { DATE_FORMAT } from "../../../../utils";
import moment from "moment";

export enum DateOperators {
	before = "before",
	after = "after",
	isEmpty = "isEmpty",
	isExpired = "isExpired",
}

const filterOptions = [
	{ value: "on", label: "On" },
	// { value: "before", label: "Before" },
	// { value: "after", label: "After" },
	{ value: "onOrBefore", label: "On or Before" },
	{ value: "onOrAfter", label: "On or After" },
	{ value: "between", label: "Between", needsTwoInputs: true },
];

export const DateColumnFilter =
	(showIsExpired = false, showIsEmpty = false) =>
	({
		column: { filterValue, setFilter },
	}: {
		column: {
			filterValue?: Record<string, any>;
			setFilter?: (value?: Record<string, any>) => void;
		};
	}) => {
		const popover = useFilterPopover();
		const [filterType, setFilterType] = useState("on");

		// Committed values — used by Apply / disabled checks.
		const [date1, setDate1] = useState<Date | null>(null);
		const [date2, setDate2] = useState<Date | null>(null);

		// Editable display text — lets the user freely retype after selecting.
		const [text1, setText1] = useState("");
		const [text2, setText2] = useState("");

		const [isEmptyState, setIsEmptyState] =
			useState<BoolToggleState>("all");
		const [expiredState, setExpiredState] =
			useState<BoolToggleState>("all");

		const getCurrentFilterLabel = () =>
			filterOptions.find((opt) => opt.value === filterType)?.label ??
			"Select Filter";

		const needsTwoInputs = () => filterType === "between";

		// Only hide date controls when isEmpty=true
		// isEmpty=false, expired=true/false all combine with date filters
		const showDateControls = isEmptyState !== "true";

		//  Convert a local date to start-of-day UTC ISO string
		const toStartOfDayUtc = (date: Date): string =>
			new Date(
				date.getFullYear(),
				date.getMonth(),
				date.getDate(),
			).toISOString();

		// Convert a local date to end-of-day (start of next day) UTC ISO string
		const toEndOfDayUtc = (date: Date): string =>
			new Date(
				date.getFullYear(),
				date.getMonth(),
				date.getDate() + 1,
			).toISOString();

		const handleIsEmptyChange = (state: BoolToggleState) => {
			setIsEmptyState(state);
			// Reset expired when isEmpty is active
			if (state !== "all") setExpiredState("all");

			// Only clear dates when isEmpty is true
			if (state === "true") {
				setDate1(null);
				setText1("");
				setDate2(null);
				setText2("");
			}
		};

		const handleExpiredChange = (state: BoolToggleState) => {
			setExpiredState(state);
			// Reset isEmpty when expired is active
			if (state !== "all") setIsEmptyState("all");
		};

		// react-datetime fires onChange with a moment (valid selection or
		// fully-valid typed input) or a raw string (mid-edit / not yet valid).
		// Keep whatever they type so the input stays editable; only commit a
		// Date when it's actually valid.
		const handleDateChange =
			(setDate: (d: Date | null) => void, setText: (s: string) => void) =>
			(momentOrString: any) => {
				if (
					momentOrString &&
					typeof momentOrString.toDate === "function"
				) {
					setDate(momentOrString.toDate());
					setText(momentOrString.format(DATE_FORMAT));
				} else {
					setText(
						typeof momentOrString === "string"
							? momentOrString
							: "",
					);
					setDate(null);
				}
			};

		// On blur, tolerantly parse the typed text. Non-strict + DATE_FORMAT
		// accepts single-digit D/M and 2-digit years in the locale's order,
		// then normalises the display (e.g. "6/6/27" -> "06/06/2027").
		const handleDateBlur =
			(setDate: (d: Date | null) => void, setText: (s: string) => void) =>
			(e: React.FocusEvent<HTMLInputElement>) => {
				const raw = e.target.value.trim();
				if (!raw) {
					setDate(null);
					setText("");
					return;
				}
				const m = moment(raw, DATE_FORMAT, false);
				if (m.isValid()) {
					setDate(m.toDate());
					setText(m.format(DATE_FORMAT));
				} else {
					// Leave their text so they can fix it.
					setDate(null);
				}
			};

		// Enter blurs the input, which fires onBlur (commit + normalise) and
		// makes react-datetime close the calendar.
		const handleDateKeyDown = (
			e: React.KeyboardEvent<HTMLInputElement>,
		) => {
			if (e.key === "Enter") {
				e.preventDefault();
				e.currentTarget.blur();
			}
		};

		const handleApply = () => {
			let filterConfig: Record<string, any> = {};

			// isEmpty is always a standalone filter
			// isEmpty=true is standalone (no dates to combine with)
			if (isEmptyState === "true") {
				filterConfig[DateOperators.isEmpty] = true;
				setFilter?.(filterConfig);
				popover.setHasActiveFilter(true);
				popover.close();
				return;
			}

			// isEmpty=false combines with date filters
			if (isEmptyState === "false")
				filterConfig[DateOperators.isEmpty] = false;

			// expired combines with date filters (both true and false)
			if (expiredState !== "all")
				filterConfig[DateOperators.isExpired] = expiredState === "true";

			// Date mode — build range filters
			switch (filterType) {
				case "on":
					if (!date1) break;
					filterConfig[DateOperators.after] = toStartOfDayUtc(date1);
					filterConfig[DateOperators.before] = toEndOfDayUtc(date1);
					break;
				case "onOrBefore":
					if (!date1) break;
					filterConfig[DateOperators.before] = toEndOfDayUtc(date1);
					break;
				case "onOrAfter":
					if (!date1) break;
					filterConfig[DateOperators.after] = toStartOfDayUtc(date1);
					break;
				case "between":
					if (!date1 || !date2) break;
					filterConfig[DateOperators.after] = toStartOfDayUtc(date1);
					filterConfig[DateOperators.before] = toEndOfDayUtc(date2);
					break;
			}

			// If nothing is set, clear the filter
			if (Object.keys(filterConfig).length === 0) {
				setFilter?.();
				popover.setHasActiveFilter(false);
				popover.close();
				return;
			}

			setFilter?.(filterConfig);
			popover.setHasActiveFilter(true);
			popover.close();
		};

		const handleClear = () => {
			setDate1(null);
			setDate2(null);
			setText1("");
			setText2("");
			setFilterType("on");
			setIsEmptyState("all");
			setExpiredState("all");
			setFilter?.();
			popover.setHasActiveFilter(false);
			popover.close();
		};

		const isApplyDisabled = () => {
			if (isEmptyState !== "all") return false;
			if (expiredState !== "all") return false;
			// If there's an active filter and everything is reset, allow Apply to clear it
			if (popover.hasActiveFilter && !date1) return false;
			if (needsTwoInputs()) return !date1 || !date2;
			return !date1;
		};

		return (
			<FilterPopover
				popoverId="popover-date-filter"
				applyDisabled={isApplyDisabled()}
				onApply={handleApply}
				onClear={handleClear}
				popoverState={popover}
			>
				{/* Date filter controls — hidden when isEmpty active or expired=true */}
				{showDateControls && (
					<>
						<div className="d-flex align-items-center justify-content-between">
							<Form.Label className="fw-bold mb-0">
								Condition
							</Form.Label>
							<Dropdown
								style={{
									flex: 1,
									maxWidth: "var(--filter-input-width, 70%)",
								}}
							>
								<Dropdown.Toggle
									size="sm"
									variant="outline-secondary"
									id="date-filter-type-dropdown"
									className="w-100 d-flex justify-content-between align-items-center"
								>
									<span>{getCurrentFilterLabel()}</span>
									<i className="fas fa-chevron-down ms-2"></i>
								</Dropdown.Toggle>
								<Dropdown.Menu
									className="py-1"
									style={{ width: "100%", minWidth: 0 }}
								>
									{filterOptions.map((option) => (
										<Dropdown.Item
											key={option.value}
											active={filterType === option.value}
											onClick={() =>
												setFilterType(option.value)
											}
											className="py-1"
											style={{ fontSize: "0.8125rem" }}
										>
											{option.label}
										</Dropdown.Item>
									))}
								</Dropdown.Menu>
							</Dropdown>
						</div>

						<div className="d-flex align-items-center justify-content-between">
							<Form.Label className="fw-bold mb-0">
								Value
							</Form.Label>
							<div
								style={{
									flex: 1,
									maxWidth: "var(--filter-input-width, 70%)",
								}}
							>
								<ReactDatetimeClass
									timeFormat={false}
									dateFormat={DATE_FORMAT}
									value={text1}
									closeOnSelect={true}
									inputProps={{
										className:
											"form-control form-control-sm",
										placeholder: needsTwoInputs()
											? `From (${DATE_FORMAT})`
											: DATE_FORMAT,
										onBlur: handleDateBlur(
											setDate1,
											setText1,
										),
										onKeyDown: handleDateKeyDown,
									}}
									onChange={handleDateChange(
										setDate1,
										setText1,
									)}
								/>
								{needsTwoInputs() && (
									<ReactDatetimeClass
										timeFormat={false}
										dateFormat={DATE_FORMAT}
										value={text2}
										closeOnSelect={true}
										inputProps={{
											className:
												"form-control form-control-sm mt-2",
											placeholder: `To (${DATE_FORMAT})`,
											onBlur: handleDateBlur(
												setDate2,
												setText2,
											),
											onKeyDown: handleDateKeyDown,
										}}
										onChange={handleDateChange(
											setDate2,
											setText2,
										)}
									/>
								)}
							</div>
						</div>
					</>
				)}
				{/* Boolean toggles */}
				{showIsEmpty && (
					<ThreeWayToggle
						label="Is Empty"
						state={isEmptyState}
						onChange={handleIsEmptyChange}
					/>
				)}
				{showIsExpired && (
					<ThreeWayToggle
						label="Is Expired"
						state={expiredState}
						onChange={handleExpiredChange}
					/>
				)}
			</FilterPopover>
		);
	};
